import { Box, Text, Flex } from '@chakra-ui/react';
import BigNumber from 'bignumber.js';
import React from 'react';

import type { Transaction } from 'types/api/transaction';

import { route } from 'nextjs/routes';

import config from 'configs/app';
import { useMultichainContext } from 'lib/contexts/multichain';
import getValueWithUnit from 'lib/getValueWithUnit';
import { currencyUnits } from 'lib/units';
import { Link } from 'toolkit/chakra/link';
import BlobEntity from 'ui/shared/entities/blob/BlobEntity';
import TextSeparator from 'ui/shared/TextSeparator';
import TxFee from 'ui/shared/tx/TxFee';
import Utilization from 'ui/shared/Utilization/Utilization';

const TxAdditionalInfoContent = ({ tx }: { tx: Transaction }) => {
  const multichainContext = useMultichainContext();

  const sectionProps = {
    borderBottom: '1px solid',
    borderColor: 'border.dividerPale',
    paddingBottom: 3,
    marginBottom: 3,
  };

  const sectionTitleProps = {
    color: 'text.secondary',
    fontWeight: 500,
    marginBottom: '6px',
    textTransform: 'uppercase',
  };

  return (
    <>
      { tx.blob_versioned_hashes && tx.blob_versioned_hashes.length > 0 && (
        <Box { ...sectionProps }>
          <Flex alignItems="center" justifyContent="space-between">
            <Text { ...sectionTitleProps }>Blobs: { tx.blob_versioned_hashes.length }</Text>
            { tx.blob_versioned_hashes.length > 3 && (
              <Link
                href={ route({ pathname: '/tx/[hash]', query: { hash: tx.hash, tab: 'blobs' } }) }
                mb={ 3 }
              >
                view all
              </Link>
            ) }
          </Flex>
          <Flex flexDir="column" rowGap={ 3 }>
            { tx.blob_versioned_hashes.slice(0, 3).map((hash, index) => (
              <Flex key={ hash } columnGap={ 2 }>
                <Box fontWeight={ 500 }>{ index + 1 }</Box>
                <BlobEntity hash={ hash } noIcon/>
              </Flex>
            )) }
          </Flex>
        </Box>
      ) }
      { !config.UI.views.tx.hiddenFields?.tx_fee && (
        <Box { ...sectionProps }>
          { (tx.stability_fee !== undefined || tx.fee.value !== null) && (
            <>
              <Text { ...sectionTitleProps }>Transaction fee</Text>
              <TxFee fontWeight="medium" tx={ tx } withUsd accuracyUsd={ 2 } rowGap={ 0 }/>
            </>
          ) }
        </Box>
      ) }
      { tx.gas_used !== null && (
        <Box { ...sectionProps }>
          <Text { ...sectionTitleProps }>Gas limit & usage by transaction</Text>
          <Flex>
            <Text fontWeight="medium">{ BigNumber(tx.gas_used).toFormat() }</Text>
            <TextSeparator color="border.dividerPale"/>
            <Text fontWeight="medium">{ BigNumber(tx.gas_limit).toFormat() }</Text>
            <Utilization isValueColor={ false } ml={ 4 } value={ Number(BigNumber(tx.gas_used).dividedBy(BigNumber(tx.gas_limit)).toFixed(2)) }/>
          </Flex>
        </Box>
      ) }
      { !config.UI.views.tx.hiddenFields?.gas_fees &&
        (tx.base_fee_per_gas !== null || tx.max_fee_per_gas !== null || tx.max_priority_fee_per_gas !== null) && (
        <Box { ...sectionProps }>
          <Text { ...sectionTitleProps }>Gas fees ({ currencyUnits.gwei })</Text>
          { tx.base_fee_per_gas !== null && (
            <Box>
              <Text as="span" color="text.secondaryDarker">Base </Text>
              <Text fontWeight="medium" as="span">{ getValueWithUnit(tx.base_fee_per_gas, 'gwei').toFormat() }</Text>
            </Box>
          ) }
          { tx.max_fee_per_gas !== null && (
            <Box mt={ 1 }>
              <Text as="span" color="text.secondaryDarker">Max </Text>
              <Text fontWeight="medium" as="span">{ getValueWithUnit(tx.max_fee_per_gas, 'gwei').toFormat() }</Text>
            </Box>
          ) }
          { tx.max_priority_fee_per_gas !== null && (
            <Box mt={ 1 }>
              <Text as="span" color="text.secondaryDarker">Max priority </Text>
              <Text fontWeight="medium" as="span">{ getValueWithUnit(tx.max_priority_fee_per_gas, 'gwei').toFormat() }</Text>
            </Box>
          ) }
        </Box>
      ) }
      { !(tx.blob_versioned_hashes && tx.blob_versioned_hashes.length > 0) && (
        <Box { ...sectionProps }>
          <Text { ...sectionTitleProps }>Others</Text>
          <Box>
            <Text as="span" color="text.secondaryDarker">Txn type </Text>
            <Text fontWeight="medium" as="span">{ tx.type }</Text>
            { tx.type === 2 && <Text as="span" ml={ 1 } color="text.pale">(EIP-1559)</Text> }
          </Box>
          <Box mt={ 1 }>
            <Text as="span" color="text.secondaryDarker">Nonce </Text>
            <Text fontWeight="medium" as="span">{ tx.nonce }</Text>
          </Box>
          <Box mt={ 1 }>
            <Text as="span" color="text.secondaryDarker">Position </Text>
            <Text fontWeight="medium" as="span">{ tx.position }</Text>
          </Box>
        </Box>
      ) }
      <Link
        px={ 3 }
        py={ 1 }
        textStyle="sm"
        fontWeight="medium"
        href={ route({ pathname: '/tx/[hash]', query: { hash: tx.hash } }, multichainContext) }>
        More details
      </Link>
    </>
  );
};

export default React.memo(TxAdditionalInfoContent);
